import { Component, effect, inject, computed, signal, untracked, OnInit, OnDestroy } from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CartService } from '../../services/cart.service';
import { LocationService } from '../../services/location.service';
import { ProductGraphqlService } from '../../services/product-graphql.service';
import { CartDrawer } from '../cart-drawer/cart-drawer';
import { debounceTime, distinctUntilChanged, switchMap, filter } from 'rxjs/operators';
import { Subscription, of } from 'rxjs';

@Component({
  selector: 'app-header',
  imports: [
    MatToolbarModule, 
    MatIconModule, 
    MatButtonModule, 
    RouterModule, 
    MatBadgeModule, 
    MatDialogModule,
    MatMenuModule,
    MatInputModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    ReactiveFormsModule
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  standalone: true
})
export class Header implements OnInit, OnDestroy {
  title = environment.title;
  protected readonly environment = environment;
  
  cartCount = computed(() => this.cart.items().length);
  cartAnimating = signal(false);
  
  searchControl = new FormControl<any>('');
  searchResults = signal<any[]>([]);
  isSearching = signal(false);
  recentSearches = signal<string[]>([]);
  private searchSub?: Subscription;
  
  showRecentSearches = computed(() => {
    const val = this.searchControl.value;
    const term = typeof val === 'string' ? val : (val?.type === 'product' ? val.data.name : val?.term);
    return this.recentSearches().length > 0 && !term && !this.isSearching();
  });
  
  private dialog = inject(MatDialog);
  public locationService = inject(LocationService);
  private productGql = inject(ProductGraphqlService);
  private router = inject(Router);

  constructor(public cart: CartService) {
    effect(() => {
      const count = this.cartCount();
      if (count > 0) {
        untracked(() => {
          this.cartAnimating.set(true);
          setTimeout(() => this.cartAnimating.set(false), 300);
        });
      }
    });
  }

  ngOnInit() {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try {
        this.recentSearches.set(JSON.parse(saved));
      } catch (e) {}
    }

    this.searchSub = this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        const strTerm = typeof term === 'string' ? term : '';
        if (!strTerm || strTerm.length < 2) {
          this.searchResults.set([]);
          this.isSearching.set(false);
          return of(null);
        }
        this.isSearching.set(true);
        return this.productGql.watchProducts(5, null, strTerm).valueChanges;
      })
    ).subscribe(result => {
      if (result?.data?.products?.edges) {
        this.searchResults.set(result.data.products.edges.map((e: any) => e.node));
      } else {
        this.searchResults.set([]);
      }
      this.isSearching.set(false);
    });
  }

  ngOnDestroy() {
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
  }

  saveRecentSearch(term: string) {
    if (!term || term.trim() === '') return;
    const current = this.recentSearches();
    const filtered = current.filter(t => t.toLowerCase() !== term.trim().toLowerCase());
    const updated = [term.trim(), ...filtered].slice(0, 5);
    this.recentSearches.set(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    const item = event.option.value;
    if (item.type === 'product') {
      this.saveRecentSearch(item.data.name);
      this.router.navigate(['/product', item.data.id]);
      this.searchControl.setValue('');
    } else if (item.type === 'recent') {
      this.saveRecentSearch(item.term);
      this.router.navigate(['/catalog'], { queryParams: { search: item.term } });
      this.searchControl.setValue('');
    }
  }

  displayFn(item: any): string {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.type === 'product' ? item.data.name : item.term;
  }

  onSearchSubmit() {
    let term = this.searchControl.value;
    if (typeof term === 'object' && term !== null) {
       term = term.type === 'product' ? term.data.name : term.term;
    }
    if (term && typeof term === 'string') {
      this.saveRecentSearch(term);
      this.router.navigate(['/catalog'], { queryParams: { search: term } });
      this.searchControl.setValue('');
    }
  }

  openCart() {
    this.dialog.open(CartDrawer, {
      position: { right: '0' },
      height: '100vh',
      width: '100%',
      maxWidth: '400px',
      panelClass: 'cart-drawer-panel',
      enterAnimationDuration: '250ms',
      exitAnimationDuration: '250ms'
    });
  }
}
